import { useOrgConfig } from "../Common/Hooks/OrgConfigHooks";
import { useEffect, useState } from 'react';
import { Button, Input, Segmented, Select, Space, Tooltip, Typography } from 'antd';
import { styled } from 'styled-components';
import DtRangeSelector from '../Common/DtRangeSelector';
import { useSelector, useDispatch } from "react-redux";
import { updateFilterData, clearFilterData, setFiltersBarExpanded, setSearchPending } from '../slices/filters/thunk';
import { StopOutlined, CheckOutlined, CloseOutlined, DashOutlined, FlagFilled, WarningOutlined } from '@ant-design/icons';
import CompanySelect from "../Common/CompanySelect";
import PersonSelect from "../Common/PersonSelect";
import CategorySelect from "../Common/CategorySelect";
import ProjectSelect from "../Common/ProjectSelect";
import AccountSelect from "../Common/AccountSelect";
import IntentTypeSelect from "../Common/IntentTypeSelect";
import TagsSelect from "../Common/TagsSelect";
import { MenuUnfoldOutlined, MenuFoldOutlined, AppstoreOutlined, SearchOutlined } from '@ant-design/icons';
import { Col, Row } from 'antd';
import MasterSearch from '../Common/MasterSearch';

const SEARCH_MODE_KEY = 'headerFiltersMode'; // 'master' | 'classic'

const { Text } = Typography;

// Tri-state filter: ignore (no filter) / only True / only False.
// `null` is stripped from the URL by utils.cleanEmpty, so "all" disables the filter.
const TRISTATE_OPTIONS = [
  { label: <Tooltip title="Vse"><DashOutlined /></Tooltip>, value: 'all' },
  { label: <Tooltip title="Da"><CheckOutlined style={{ color: '#52c41a' }} /></Tooltip>, value: 'yes' },
  { label: <Tooltip title="Ne"><CloseOutlined style={{ color: '#ff4d4f' }} /></Tooltip>, value: 'no' },
];

const triStateToSegment = (v) =>
  v === '1' || v === 1 ? 'yes' : v === '0' || v === 0 ? 'no' : 'all';

const segmentToFilter = (seg) => (seg === 'yes' ? 1 : seg === 'no' ? 0 : null);

// Status: single-select over two mutually-exclusive leaf filters.
// Icons mirror what's shown in the intents list (red flag / orange warning triangle).
const STATUS_OPTIONS = [
  { label: <Tooltip title="Vse"><DashOutlined /></Tooltip>, value: 'all' },
  { label: <Tooltip title="Označeno"><FlagFilled style={{ color: '#ff4d4f' }} /></Tooltip>, value: 'flagged' },
  { label: <Tooltip title="Z opozorilom"><WarningOutlined style={{ color: 'orange' }} /></Tooltip>, value: 'warning' },
];

// Year filter options: current year + 1 down to 2018. Used only on pages that
// opt in via the `shown` whitelist (e.g. the projects list).
const YEAR_OPTIONS = Array.from(
  { length: new Date().getFullYear() + 1 - 2018 + 1 },
  (_, i) => {
    const y = new Date().getFullYear() + 1 - i;
    return { label: y, value: y };
  }
);

const isTrue = (v) => v === '1' || v === 1;
const statusToValue = (f) =>
  isTrue(f.is_flagged) ? 'flagged' : isTrue(f.has_warning) ? 'warning' : 'all';
const valueToStatusFilter = (v) => ({
  is_flagged: v === 'flagged' ? 1 : null,
  has_warning: v === 'warning' ? 1 : null,
});

const HeaderFiltersWrapper = styled.div`
  width: 100%;
  height: 100%;
  flex: 1;
  line-height: normal !important;
  padding-top: 10px;

  * {
    line-height: normal !important;
  }
`;


const SearchContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  max-width: 475px;

  input {
    width: 100%;
  }
`;

// The mode toggle lives in a fixed gutter on the left of the bar, outside both
// mode branches, so it stays put when the layout underneath it swaps. It used
// to be rendered after the master input (far right) and before the classic
// search (far left), so it jumped across the whole bar on every click.
const FiltersRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
`;

// Fixed height rather than centring on the neighbouring row: the master input
// grows as chips are added, and the toggle must not drift with it.
const ToggleGutter = styled.div`
  flex: none;
  display: flex;
  align-items: center;
  height: 32px;
`;

const FiltersBody = styled.div`
  flex: 1 1 auto;
  min-width: 0;
`;


const HeaderFilters = ({ darkMode, handleToggleMode }) => {
  const dispatch = useDispatch();

  // Get hidden and shown lists and mode
  const hiddenFilters = useSelector((state) => state.Filterdata.hidden);
  const shownFilters = useSelector((state) => state.Filterdata.shown);
  const disabledFilters = useSelector((state) => state.Filterdata.disabled);

  const filtersdata = useSelector((state) => state.Filterdata.data);
  // Whether this page offers the unified "master search" at all (set per-page via
  // setInitialFilterConf). When false, only the classic filters are available.
  const masterAllowed = useSelector((state) => state.Filterdata.masterSearch);
  const [searchValue, setSearchValue] = useState(filtersdata?.search || '');
  const [mode, setMode] = useState(
    () => localStorage.getItem(SEARCH_MODE_KEY) || 'master'
  );

  // Pages without master search are always classic, regardless of the saved pref.
  const effectiveMode = masterAllowed ? mode : 'classic';

  const toggleMode = () => {
    const next = mode === 'master' ? 'classic' : 'master';
    setMode(next);
    localStorage.setItem(SEARCH_MODE_KEY, next);
  };

  // Utility function to check visibility of a filter key. Visibility is driven
  // purely by the page's own setInitialFilterConf, never by the URL — a hash
  // param must never be able to hide the bar (and with it the clear button),
  // or the user has no way back out of it.
  const orgConfig = useOrgConfig();
  const isFilterVisible = (key) => {
    if (hiddenFilters.includes('*')){
      return false;
    }
    if (hiddenFilters.includes(key)){
      return false;
    }
    return true
  };

  const checkboxGroupVisible = isFilterVisible('intent_checkboxes_group');
  // "Plačano" can also be opted into on its own by a page that hides the group.
  const paidFilterVisible = checkboxGroupVisible || shownFilters.includes('is_paid');

  // Check if ANY filter is visible (main + expanded)
  const filterKeys = [
    'date_range',
    'search',
    'relateds_group',
    'intent_type',
    'intent_checkboxes_group',
  ];

  // Debounced sync for the CLASSIC free-text input only. In master mode this
  // component's `searchValue` stays stale ('') because the classic input isn't
  // rendered, so without this guard the effect would wipe a search set by
  // MasterSearch ~750ms after it's applied.
  useEffect(() => {
    if (effectiveMode !== 'classic') return;
    const applied = filtersdata?.search || '';
    // Nothing pending: keep tables responsive but not stuck in a loading state.
    if (searchValue === applied) {
      dispatch(setSearchPending(false));
      return;
    }
    // A change is typed but not yet applied — flag it so tables show loading now.
    dispatch(setSearchPending(true));
    const handler = setTimeout(() => {
      dispatch(updateFilterData({ search: searchValue }));
      // searchPending is cleared by the re-run of this effect once `applied`
      // catches up, which lines up with the page's own fetch loading — no gap.
    }, 750);
    return () => clearTimeout(handler);
  }, [searchValue, dispatch, filtersdata?.search, effectiveMode]);

  // When switching into classic mode, seed the input from the current store value
  // so an active search (possibly set in master mode) shows up and isn't wiped.
  useEffect(() => {
    if (effectiveMode === 'classic') {
      setSearchValue(filtersdata?.search || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveMode]);


  // If everything is hidden, render nothing in either mode.
  const allHidden = () => hiddenFilters.includes('*');

  const modeToggle = (
    <Tooltip title={mode === 'master' ? 'Klasični filtri' : 'Pametno iskanje'}>
      <Button
        size="small"
        onClick={toggleMode}
        icon={mode === 'master' ? <AppstoreOutlined /> : <SearchOutlined />}
      />
    </Tooltip>
  );

  if (allHidden()) {
    return null;
  }

  // Nothing to show: no master search and every classic filter hidden.
  const anyClassicVisible = ['search', 'dt_range', 'relateds_group', 'intent_type', 'intent_checkboxes_group']
    .some(isFilterVisible);
  if (!masterAllowed && !anyClassicVisible) {
    return null;
  }

  if (effectiveMode === 'master') {
    return (
      <HeaderFiltersWrapper>
        <FiltersRow>
          <ToggleGutter>{modeToggle}</ToggleGutter>
          <FiltersBody>
            <MasterSearch />
            {/* Date range stays a dedicated control below the master input,
                roughly where it sits in the classic filter layout. */}
            {isFilterVisible('dt_range') && (
              <div style={{ marginTop: 12 }}>
                <DtRangeSelector
                  value={filtersdata.date_range}
                  onChange={(val) => dispatch(updateFilterData({ date_range: val }))}
                  disabled={disabledFilters.includes('dt_range')}
                />
              </div>
            )}
          </FiltersBody>
        </FiltersRow>
      </HeaderFiltersWrapper>
    );
  }

  return (
    <HeaderFiltersWrapper>
      {/* <pre>{JSON.stringify(filtersdata, null, 2)}</pre> */}

      <FiltersRow>
        {masterAllowed && <ToggleGutter>{modeToggle}</ToggleGutter>}
        <FiltersBody>
          <Row style={{ width: '100%', height: '100%'}} gutter={[8, 8]} align="middle">
            {/* Search + (optional) project year on one line, date range below */}
            <Col span={10}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {isFilterVisible('search') && (
                  <SearchContainer style={{ marginBottom: 0, flex: '1 1 auto' }}>
                    <Button onClick={() => { setSearchValue(''); dispatch(clearFilterData()); }}>
                      <StopOutlined />
                    </Button>
                    <Input
                      placeholder="Iskanje"
                      bordered
                      value={searchValue}
                      onChange={(e) => setSearchValue(e.target.value)}
                      disabled={disabledFilters.includes('search')}
                    />
                  </SearchContainer>
                )}

                {/* Project year (opt-in per page via the `shown` whitelist) */}
                {shownFilters.includes('project_year') && (
                  <Select
                    size="small"
                    allowClear
                    placeholder="Leto"
                    style={{ width: 130, flex: 'none' }}
                    options={YEAR_OPTIONS}
                    value={filtersdata.project_year}
                    onChange={(val) => dispatch(updateFilterData({ project_year: val ?? null }))}
                  />
                )}
              </div>

              {isFilterVisible('dt_range') && (
                <div style={{ marginTop: 20 }}>
                  <DtRangeSelector
                    value={filtersdata.date_range}
                    onChange={(val) => dispatch(updateFilterData({ date_range: val }))}
                    disabled={disabledFilters.includes('dt_range')}
                  />
                </div>
              )}
            </Col>

            {/* Accounts / Tags / Companies / Projects */}

            {isFilterVisible('relateds_group') && (
              <Col span={8}>
                <Row gutter={[8, 8]}>
                  {isFilterVisible('account') && (
                    <Col span={8} style={{ display: 'flex' }}>
                      <AccountSelect
                        multiple
                        size="small"
                        minWidth={0}
                        value={filtersdata.account || []}
                        onChange={(val) => dispatch(updateFilterData({ account: val }))}
                        placeholder={'Računi'}
                      />
                    </Col>
                  )}

                  {isFilterVisible('tags') && (
                    <Col span={8} style={{ display: 'flex' }}>
                      <TagsSelect
                        multiple
                        size="small"
                        minWidth={0}
                        allowCreate={false}
                        allowNoValue
                        value={filtersdata.tags_filters}
                        onChange={(val) => dispatch(updateFilterData({ tags_filters: val }))}
                        placeholder={'Oznake'}
                      />
                    </Col>
                  )}

                  {isFilterVisible('companies') && (
                    <Col span={8} style={{ display: 'flex' }}>
                      <CompanySelect
                        multiple
                        size="small"
                        minWidth={0}
                        allowCreate={false}
                        allowNoValue
                        value={filtersdata.companies}
                        onChange={(val) => dispatch(updateFilterData({ companies: val }))}
                        placeholder={'Podjetje'}
                      />
                    </Col>
                  )}

                  {isFilterVisible('persons') && (
                    <Col span={8} style={{ display: 'flex' }}>
                      <PersonSelect
                        multiple
                        size="small"
                        minWidth={0}
                        allowCreate={false}
                        value={filtersdata.persons}
                        onChange={(val) => dispatch(updateFilterData({ persons: val }))}
                        placeholder={'Oseba'}
                      />
                    </Col>
                  )}

                  {isFilterVisible('category') && (
                    <Col span={8} style={{ display: 'flex' }}>
                      <CategorySelect
                        multiple
                        size="small"
                        minWidth={0}
                        allowCreate={false}
                        value={filtersdata.category}
                        onChange={(val) => dispatch(updateFilterData({ category: val }))}
                        placeholder={'Kategorija'}
                      />
                    </Col>
                  )}

                  {isFilterVisible('projects') && (
                    <Col span={8} style={{ display: 'flex' }}>
                      <ProjectSelect
                        multiple
                        size="small"
                        minWidth={0}
                        allowCreate={false}
                        allowNoValue
                        value={filtersdata.projects}
                        onChange={(val) => dispatch(updateFilterData({ projects: val }))}
                        placeholder={'Projekti'}
                      />
                    </Col>
                  )}
                </Row>
          </Col>
      )}

        {/* Intent Type */}
        <Col span={2}>
          {isFilterVisible('intent_type') && (
            <IntentTypeSelect
              small
              mode="multi"
              value={filtersdata.type}
              onChange={(val) => dispatch(updateFilterData({ type: val }))}
            />
          )}
        </Col>

        {/* Paid / Issued tri-state filters.
            Gated per control, not as a block: a page may hide the group yet still
            need "Plačano" (the insights pages do -- without it their totals mix
            realised spending with what is only planned). Opting in via `shown`
            keeps the control in the position it occupies on the transactions
            list, rather than relocating it somewhere unfamiliar. */}
        <Col span={3}>
          {(checkboxGroupVisible || paidFilterVisible) && (
            <Space direction="vertical" size={2} style={{ width: '100%' }}>
              {paidFilterVisible && (
              <Space size={4}>
                <Text style={{ fontSize: 11 }}>Plačano:</Text>
                <Segmented
                  size="small"
                  options={TRISTATE_OPTIONS}
                  value={triStateToSegment(filtersdata.is_paid)}
                  onChange={(val) => dispatch(updateFilterData({ is_paid: segmentToFilter(val) }))}
                />
              </Space>
              )}
              {checkboxGroupVisible && orgConfig.track_issuance && (
                <Space size={4}>
                  <Text style={{ fontSize: 11 }}>Izdano:</Text>
                  <Segmented
                    size="small"
                    options={TRISTATE_OPTIONS}
                    value={triStateToSegment(filtersdata.is_issued)}
                    onChange={(val) => dispatch(updateFilterData({ is_issued: segmentToFilter(val) }))}
                  />
                </Space>
              )}
              {checkboxGroupVisible && (
              <Space size={4}>
                <Text style={{ fontSize: 11 }}>Status:</Text>
                <Segmented
                  size="small"
                  options={STATUS_OPTIONS}
                  value={statusToValue(filtersdata)}
                  onChange={(val) => dispatch(updateFilterData(valueToStatusFilter(val)))}
                />
              </Space>
              )}
            </Space>
          )}
        </Col>

      </Row>
        </FiltersBody>
      </FiltersRow>
    </HeaderFiltersWrapper>
  );
};

export default HeaderFilters;
